import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { NotificationResponse } from '@/types/notification';
import { showApiError } from '@/utils/apiError';

export function NotificationsCenterPage() {
  const pagination = usePagination(20);
  const [rows, setRows] = useState<NotificationResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await notificationsApi.list({ page: pagination.page, size: pagination.size });
      setRows(page.content ?? []);
      pagination.applyPageResponse(page);
    } catch (err) {
      showApiError(err, 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      void load();
    } catch (err) {
      showApiError(err, 'Failed to mark all read');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Notifications"
        description="In-app alerts across all modules — email and mobile digests use your organization settings"
        breadcrumbs={[{ label: 'My Work', to: '/work' }, { label: 'Notifications' }]}
        actions={
          <>
            <Button variant="secondary" onClick={() => void markAllRead()}>
              Mark all read
            </Button>
            <Link to="/work" className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
              My Work →
            </Link>
          </>
        }
      />

      <ul className="space-y-2">
        {loading && <li className="text-sm text-slate-500">Loading…</li>}
        {!loading && rows.length === 0 && (
          <li className="card p-6 text-sm text-slate-500">No notifications yet.</li>
        )}
        {rows.map((n) => (
          <li
            key={n.id}
            className={`card p-4 text-sm ${!n.readFlag ? 'ring-2 ring-primary-500/20' : ''}`}
          >
            <div className="flex justify-between gap-2">
              <p className="font-medium">{n.title}</p>
              <span className="text-xs text-slate-500">
                {n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}
              </span>
            </div>
            <p className="mt-1 text-slate-600 dark:text-slate-400">{n.message}</p>
            {n.notificationType && (
              <p className="mt-1 text-xs uppercase text-slate-400">{n.notificationType}</p>
            )}
          </li>
        ))}
      </ul>
      <TablePagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        loading={loading}
        hasRows={rows.length > 0}
      />
    </div>
  );
}
